'use strict'

import axios from 'axios'
import { actorKind, conflictWarning } from '../../../shared/designer-sync.js'

const tabId = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `tab-${Date.now().toString(36)}-local`

let socket = null
let applicationUniqueTitle = ''
let localUserKey = ''
let generation = 0
let reconnectTimer = null
let warning = ''
const changeListeners = new Set()
const warningListeners = new Set()

function designerTabId () {

  return tabId
}

function currentDesignerWarning () {

  return warning
}

function setWarning (next) {

  warning = next || ''
  warningListeners.forEach((listener) => listener(warning))
}

function clearDesignerWarning () {

  setWarning('')
}

function onDesignerChange (listener) {

  changeListeners.add(listener)

  return () => changeListeners.delete(listener)
}

function onDesignerWarning (listener) {

  warningListeners.add(listener)
  listener(warning)

  return () => warningListeners.delete(listener)
}

function raiseDesignerWarning (change, resourceLabel) {

  const kind = actorKind({
    localTabId: tabId,
    remoteTabId: change && change.actor && change.actor.tabId,
    localUserKey,
    remoteUserKey: change && change.actor && change.actor.userKey
  })

  if (kind === 'self') return

  const label = resourceLabel || (change && change.label) || 'this application'

  setWarning(conflictWarning({
    kind,
    actorLabel: change && change.actor && change.actor.userLabel,
    resourceLabel: label
  }))
}

function stopSocket () {

  if (reconnectTimer) {

    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }

  if (socket) {

    socket.onclose = null
    socket.close()
    socket = null
  }
}

function scheduleReconnect (gen) {

  if (gen !== generation || !applicationUniqueTitle) return

  reconnectTimer = setTimeout(() => {

    reconnectTimer = null
    openSocket(gen)
  }, 3000)
}

async function openSocket (gen) {

  if (gen !== generation || !applicationUniqueTitle) return

  let ticket = ''

  try {

    const response = await axios.get(`${window.__SUPERTURBO_API__}/designer-sync/ticket`)

    ticket = response && response.data && response.data.ticket
  } catch (error) {

    scheduleReconnect(gen)

    return
  }

  if (gen !== generation || !ticket) {

    if (gen === generation) scheduleReconnect(gen)

    return
  }

  let next

  try {

    next = new WebSocket(VUE_APP_APP_SERVER_WS_URL)
  } catch (error) {

    scheduleReconnect(gen)

    return
  }

  if (gen !== generation) {

    next.close()

    return
  }

  socket = next

  next.onopen = () => {

    if (gen !== generation) return

    next.send(JSON.stringify({
      type: 'designer_subscribe',
      ticket,
      applicationUniqueTitle,
      tabId
    }))
  }

  next.onmessage = (event) => {

    let message

    try {

      message = JSON.parse(event.data)
    } catch (error) {

      return
    }

    if (!message || gen !== generation) return

    if (message.type === 'designer_subscribed') {

      localUserKey = message.userKey || ''

      return
    }

    if (message.type !== 'designer_change') return

    if (message.applicationUniqueTitle && message.applicationUniqueTitle !== applicationUniqueTitle) return

    const kind = actorKind({
      localTabId: tabId,
      remoteTabId: message.actor && message.actor.tabId,
      localUserKey,
      remoteUserKey: message.actor && message.actor.userKey
    })

    if (kind === 'self') return

    changeListeners.forEach((listener) => {

      try {

        const result = listener(message)

        if (result && typeof result.then === 'function') {

          result.catch((error) => console.warn('Designer sync failed:', error))
        }
      } catch (error) {

        console.warn('Designer sync failed:', error)
      }
    })
  }

  next.onerror = () => {}

  next.onclose = () => {

    if (socket === next) socket = null

    scheduleReconnect(gen)
  }
}

function startDesignerSync (application) {

  if (!application) return

  if (application === applicationUniqueTitle && socket && socket.readyState <= 1) return

  generation += 1
  applicationUniqueTitle = application
  localUserKey = ''
  stopSocket()
  openSocket(generation)
}

function stopDesignerSync () {

  generation += 1
  applicationUniqueTitle = ''
  stopSocket()
}

export {
  designerTabId,
  currentDesignerWarning,
  clearDesignerWarning,
  onDesignerChange,
  onDesignerWarning,
  raiseDesignerWarning,
  startDesignerSync,
  stopDesignerSync
}
